import React, { useContext, useEffect, useState } from 'react'
import { View, Text, StyleSheet, Image, Pressable } from 'react-native'

import {
  BG_STYLES,
  Button,
  ChevronDownIcon,
  COLORS,
  COMMON_STYLES,
  LazyImage,
  Spinner,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from '@dehaat/dds'
import { ROUTES as FARMER_360_ROUTES } from 'app/farmer360/routes/types'
import { ResponsiveContext } from 'app/context/ResponsiveUIContext'
import { DEHAAT_LOGO, TRACTOR_IMAGE } from 'app/assets/images'
import { rnNavigation } from 'app/utils/appConfig'
import fetchUserDetails from 'app/apis/fetchUserDetails'
import { UserInfo } from 'app/models/User'
import SearchInput from 'app/components/reusable/SearchInput'
import fetchFarmerList from 'app/farmer360/apis/fetchFarmerList'
import { Farmer } from 'app/farmer360/models/farmer'
import FarmerCard from 'app/farmer360/components/farmerList/FarmerCard'
import { DEFAULT_AVATOR_COLORS } from 'app/farmer360/constants/common'
import ToastAndroid from 'app/mocks/ToastAndroid'
import NoFarmerFound from 'app/farmer360/components/farmerList/NoFarmerFound'
import { TECH_SUPPORT_EMAIL, TRACTTOR_GIF_URL } from 'app/constants/common'
import { updateMixpanelUserProperties } from 'app/farmer360/utils/tracking'
import { DEFAULT_ROLE, ROLE_MAP } from 'app/constants/request'
import Dropdown from 'app/components/Dropdown'

const Dashboard = () => {
  const { mediaStyles } = useContext(ResponsiveContext)
  const [info, setInfo] = useState<UserInfo>()

  const [searchQuery, setSearchQuery] = useState('')
  const [error, setError] = useState(false)
  const [farmerList, setFarmerList] = useState<Farmer[]>([])
  const [isPageLoading, setIsPageLoading] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [farmerNotExist, setFarmerNotExistFlag] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [data, setData] = useState<string[]>([])
  const [selectedRole, setSelectedRole] = useState(DEFAULT_ROLE)
  const searchFarmers = async () => {
    if (searchQuery.length === 10) {
      setIsLoading(true)
      const data = await fetchFarmerList(searchQuery)
      if (data?.results) {
        setFarmerNotExistFlag(data?.results.length === 0)
        setFarmerList(data.results)
      } else {
        ToastAndroid.show(
          'Something went wrong. Please reach out to tech support team.',
          ToastAndroid.SHORT,
        )
        setFarmerList([])
      }
      setIsLoading(false)
    } else {
      setError(true)
    }
  }

  useEffect(() => {
    const fetchRoles = async () => {
      const userData = await fetchUserDetails()
      if (userData) {
        setInfo(userData)

        const userGroups = userData.groups || []
        const matchedRoleKeys = Object.keys(ROLE_MAP).filter(roleKey =>
          userGroups.includes(roleKey),
        )
        const finalRoles =
          matchedRoleKeys.length > 0 ? [...matchedRoleKeys, 'FRM'] : ['FRM']

        setData(finalRoles)

        const savedRole = localStorage.getItem('UserRole')
        if (savedRole && finalRoles.includes(savedRole)) {
          setSelectedRole(savedRole)
        } else {
          setSelectedRole(DEFAULT_ROLE)
          localStorage.setItem('UserRole', DEFAULT_ROLE)
        }
        setData(finalRoles)
        const mixpanelUser = {
          $name: userData.user_name,
          $mobile: userData.mobile,
          $login_id: userData.email,
        }
        updateMixpanelUserProperties(mixpanelUser, '$login_id')
      }
    }
    fetchRoles().finally(() => setIsPageLoading(false))
  }, [])

  return (
    <View style={[styles.container, COMMON_STYLES.flex1]}>
      {(isPageLoading || isLoading) && <Spinner type="on-screen" />}
      <View style={[COMMON_STYLES.row, COMMON_STYLES.justifyBetween]}>
        <Text
          style={[
            TYPOGRAPHY.TEXT2,
            VARIANTS_STYLES.MEDIUM,
            TEXT_STYLES.textNeutral80,
          ]}
        >
          Hi, {info?.user_name}{' '}
        </Text>
        <Pressable
          style={[
            COMMON_STYLES.row,
            COMMON_STYLES.alignItemsCenter,
            BG_STYLES.bgNeutral10,
            COMMON_STYLES.ph1,
            COMMON_STYLES['pv1/2'],
            { borderRadius: 30 },
          ]}
          onPress={() => setShowDropdown(!showDropdown)}
        >
          {info?.profile_image ? (
            <Image
              source={{ uri: info.profile_image }}
              style={[styles.image, COMMON_STYLES['mr0.5']]}
            />
          ) : (
            <View
              style={[
                COMMON_STYLES.alignItemsCenter,
                COMMON_STYLES.justifyCenter,
                styles.image,
                BG_STYLES.bgPrimary100,
                COMMON_STYLES['mr0.5'],
              ]}
            >
              <Text style={[TEXT_STYLES.textWhite, TYPOGRAPHY.HEADING1]}>
                {info?.user_name?.[0]}
              </Text>
            </View>
          )}
          <View style={[COMMON_STYLES['mr0.5'], { flexDirection: 'column' }]}>
            <Text
              style={[
                TYPOGRAPHY.TEXT1,
                VARIANTS_STYLES.MEDIUM,
                TEXT_STYLES.textNeutral80,
              ]}
            >
              {info?.user_name || ''}
            </Text>
            <Text
              style={[
                TYPOGRAPHY.CAPTION1,
                VARIANTS_STYLES.BASE,
                TEXT_STYLES.textNeutral80,
              ]}
            >
              <Text>{ROLE_MAP[selectedRole] || selectedRole}</Text>
            </Text>
          </View>
          <ChevronDownIcon height={14} width={14} />
        </Pressable>
        <Dropdown
          showDropdown={showDropdown}
          setShowDropdown={setShowDropdown}
          data={data}
          selectedRole={selectedRole}
          setSelectedRole={setSelectedRole}
        />
      </View>
      <View
        style={mediaStyles({
          desktopStyle: [
            COMMON_STYLES.alignItemsCenter,
            COMMON_STYLES['mt3/2'],
          ],
        })}
      >
        <Image source={DEHAAT_LOGO} style={{ height: 57, width: 157 }} />
        <Text
          style={[
            TYPOGRAPHY.TEXT2,
            VARIANTS_STYLES.MEDIUM,
            TEXT_STYLES.textNeutral80,
            COMMON_STYLES['mt3/2'],
          ]}
        >
          Farmer Relationship Management (FRM) Portal
        </Text>
      </View>
      <View
        style={[
          COMMON_STYLES.mt2,
          COMMON_STYLES.alignItemsCenter,
          COMMON_STYLES.flex1,
        ]}
      >
        <View style={[{ maxWidth: 960, width: '100%' }]}>
          <Text
            style={[
              TYPOGRAPHY.HEADING3,
              VARIANTS_STYLES.MEDIUM,
              TEXT_STYLES.textNeutral80,
            ]}
          >
            Search Farmer
          </Text>
          <View
            style={[
              COMMON_STYLES.row,
              COMMON_STYLES['mt1/2'],
              COMMON_STYLES.flex1,
            ]}
          >
            <View style={COMMON_STYLES.flex1}>
              <SearchInput
                placeholder={'Search by Phone No.'}
                onChange={text => {
                  if (text.length <= 10 && !isNaN(Number(text))) {
                    setSearchQuery(text)
                    if (error) setError(false)
                  }

                  setFarmerNotExistFlag(false)
                  setFarmerList([])
                }}
                value={searchQuery}
                inputStyle={styles.h4}
                inputContainerStyle={COMMON_STYLES.br1}
                onSubmitEditing={searchFarmers}
                error={error}
                errorText={'Please enter a 10 digit mobile number'}
              />
            </View>

            <Button
              onClick={searchFarmers}
              text={'Search'}
              buttonStyle={[
                styles.searchBtn,
                styles.h4,
                COMMON_STYLES.br1,
                COMMON_STYLES['ml0.5'],
              ]}
              textStyle={TYPOGRAPHY.HEADING3}
            />
          </View>
          <View style={[COMMON_STYLES.fullWidth, COMMON_STYLES.pv1]}>
            {farmerList.map((item, index) => (
              <FarmerCard
                key={item.id}
                imageURL={null}
                name={item.full_name}
                address={item.short_address}
                onClick={() =>
                  rnNavigation({
                    name: FARMER_360_ROUTES.FARMER_360_HOME,
                    params: {
                      farmerId: item.id,
                      name: item.full_name,
                      phoneNumber: item.phone_number,
                    },
                  })
                }
                phoneNumber={item.phone_number}
                backgroundColor={DEFAULT_AVATOR_COLORS[index % 4]}
              />
            ))}
            {farmerNotExist && (
              <NoFarmerFound
                farmerNumber={searchQuery}
                onCreateProfileClick={() =>
                  rnNavigation({
                    name: FARMER_360_ROUTES.ADD_FARMER_DETAILS,
                    params: {
                      name: isNaN(Number(searchQuery)) ? searchQuery : '',
                      phoneNumber: isNaN(Number(searchQuery))
                        ? ''
                        : searchQuery,
                    },
                  })
                }
              />
            )}
          </View>
        </View>
      </View>
      <View
        style={[
          COMMON_STYLES.row,
          COMMON_STYLES.alignItemsEnd,
          COMMON_STYLES.flex1,
        ]}
      >
        <LazyImage
          imageUrl={TRACTTOR_GIF_URL}
          imgStyles={styles.img99X168}
          fallBackIcon={
            <Image source={TRACTOR_IMAGE} style={styles.img99X168} />
          }
        />
        <View
          style={[
            styles.footer,
            mediaStyles({ desktopStyle: [COMMON_STYLES.row, styles.h68] }),
          ]}
        >
          <View style={[COMMON_STYLES.flex1, COMMON_STYLES.justifyCenter]}>
            <Text
              style={[
                TYPOGRAPHY.CAPTION1,
                VARIANTS_STYLES.MEDIUM,
                TEXT_STYLES.textNeutral70,
                COMMON_STYLES.textCenter,
              ]}
            >
              For platform & tech related issues, mail to : {TECH_SUPPORT_EMAIL}
            </Text>
          </View>
          <View>
            <Text
              style={[
                TYPOGRAPHY.CAPTION1,
                VARIANTS_STYLES.MEDIUM,
                TEXT_STYLES.textNeutral70,
              ]}
            >
              All rights reserved. Dehaat@2025
            </Text>
          </View>
        </View>
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: 16,
    paddingHorizontal: 40,
  },
  h4: {
    height: 64,
  },
  h68: {
    height: 68,
  },
  searchBtn: {
    width: 194,
  },
  footer: {
    height: 96,
    alignItems: 'center',
    flex: 1,
    borderTopWidth: 1,
    borderColor: COLORS.neutral40,
    marginLeft: 32,
  },
  img99X168: {
    height: 99,
    width: 168,
  },
  image: {
    borderRadius: 99,
    width: 32,
    height: 32,
  },
})

export default Dashboard
